import { createReducer, on } from '@ngrx/store';
import { Consignee } from '../../shared/models/api/consignee';
import { Consignor } from '../../shared/models/api/consignor';
import { Node } from '../../shared/models/api/node';
import {
  addCustomerDropMultipleParcel,
  removeCustomerDropMultipleParcel,
  resetCustomerDropMultiple,
  setCustomerDropMultipleActiveParcelRef,
  setCustomerDropMultipleConsignee,
  setCustomerDropMultipleConsignor,
  setCustomerDropMultipleMarketingOptIn,
  setCustomerDropMultipleDestination,
  setCustomerDropMultipleOptions,
  setCustomerDropMultipleParcels,
} from '../actions/customer-drop-multiple.actions';

/**
 * One scanned parcel in the batch, carrying its OWN receiver and destination.
 */
export interface CustomerDropMultipleParcel {
  parcelRef: string;
  consignee?: Consignee;
  destination?: Node;
  /** Captured ahead of the drop, so receiver and destination arrived filled. */
  preInducted?: boolean;
}

/** Which drop size the operator picked on the options step. */
export type CustomerDropMode = 'single' | 'multiple';

export interface CustomerDropMultipleOptions {
  /** Defaults to 'single' so F10 on the options step continues straight away. */
  mode: CustomerDropMode;
  /**
   * The flag the rest of the flow reads. Both modes run the SAME steps for now,
   * so this only records the operator's choice.
   */
  multiple: boolean;
}

export interface CustomerDropMultipleState {
  options: CustomerDropMultipleOptions;
  /** Per-parcel receiver and destination. */
  parcels: CustomerDropMultipleParcel[];
  /** Shared across the whole batch. */
  consignor: Consignor;
  /** Which per-parcel sub-form the UI currently has expanded. */
  activeParcelRef: string | null;
  /** Sender consent to marketing. Defaults to opted in; F2 opts out. */
  marketingOptIn: boolean;
}

export const initialState: CustomerDropMultipleState = {
  options: { mode: 'single', multiple: false },
  parcels: [],
  consignor: {
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    specialInstructions: '',
    languagePreference: '',
  },
  activeParcelRef: null,
  marketingOptIn: true,
};

export const customerDropMultipleReducer = createReducer(
  initialState,

  on(setCustomerDropMultipleOptions, (state, { options }) => ({
    ...state,
    options,
  })),

  on(setCustomerDropMultipleParcels, (state, { parcels }) => ({
    ...state,
    parcels,
  })),

  on(addCustomerDropMultipleParcel, (state, { parcel }) =>
    state.parcels.some((p) => p.parcelRef === parcel.parcelRef)
      ? state
      : { ...state, parcels: [...state.parcels, parcel] }
  ),

  on(removeCustomerDropMultipleParcel, (state, { parcelRef }) => ({
    ...state,
    parcels: state.parcels.filter((p) => p.parcelRef !== parcelRef),
    activeParcelRef:
      state.activeParcelRef === parcelRef ? null : state.activeParcelRef,
  })),

  on(setCustomerDropMultipleConsignee, (state, { parcelRef, consignee }) => ({
    ...state,
    parcels: state.parcels.map((p) =>
      p.parcelRef === parcelRef ? { ...p, consignee } : p
    ),
  })),

  on(setCustomerDropMultipleDestination, (state, { parcelRef, destination }) => ({
    ...state,
    parcels: state.parcels.map((p) =>
      p.parcelRef === parcelRef ? { ...p, destination } : p
    ),
  })),

  on(setCustomerDropMultipleConsignor, (state, { consignor }) => ({
    ...state,
    consignor,
  })),

  on(setCustomerDropMultipleActiveParcelRef, (state, { parcelRef }) => ({
    ...state,
    activeParcelRef: parcelRef,
  })),

  on(setCustomerDropMultipleMarketingOptIn, (state, { marketingOptIn }) => ({
    ...state,
    marketingOptIn,
  })),

  on(resetCustomerDropMultiple, () => ({
    ...initialState,
  }))
);
